import { useEffect, useRef } from 'react';

// Shop-floor terminal curfew: call `onLogout` when the clock CROSSES the
// curfew hour (default 19:00) while the user is logged in on a *visible*
// terminal page.
//
// Design notes — these are the behaviours that matter:
// - Crossing-based, not wall-clock-based: someone who logs in at 19:03 is
//   never logged straight back out; the boundary has to pass while they're
//   on the page.
// - Visibility-gated: a terminal tab left open in the background can't kill
//   the session while the user works in another tab (the old v1 effect
//   POSTed /api/auth/logout/ from background tabs, which nuked the shared
//   session for every page in the browser).
// - A crossing that happens while the tab is hidden doesn't count, and is
//   not retro-fired when the tab becomes visible again.
// - Fires at most once per crossing; the next day's crossing fires again.
export default function useTerminalCurfew({
  user,
  onLogout,
  hour = 19,
  intervalMs = 60000,
}) {
  const lastTickRef = useRef(null);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  useEffect(() => {
    if (!user) return undefined;

    // Mount (login) time — the curfew boundary must be crossed AFTER this.
    lastTickRef.current = new Date();

    const tick = () => {
      const now = new Date();
      const prev = lastTickRef.current || now;
      // Always advance, even when hidden: a crossing that happened while
      // hidden must not retro-fire on the next visible tick.
      lastTickRef.current = now;

      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      const cutoff = new Date(now);
      cutoff.setHours(hour, 0, 0, 0);
      if (prev < cutoff && now >= cutoff) {
        onLogoutRef.current();
      }
    };

    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [user?.id, hour, intervalMs]); // eslint-disable-line react-hooks/exhaustive-deps
}
